var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select } from '@web/components/ui/Form/Select';
import { classNames } from '@web/utility';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
/** Minimum horizontal gap per tab side; below this, tabs scroll instead of shrinking. */
var MIN_SLOT_PADDING_PX = 5;
var CALCULATED_TAB_NAV_LABEL_CLASS = 'whitespace-nowrap text-sm font-medium leading-none tracking-tight';
var measureCalculatedTabNavLabelWidths = function (measureRoot) {
    var spans = measureRoot.querySelectorAll('[data-tab-label-measure]');
    return Array.from(spans).map(function (span) {
        return Math.ceil(span.getBoundingClientRect().width);
    });
};
var computeCalculatedTabNavLayout = function (containerWidth, labelWidths, tabIds, minSlotPaddingPx) {
    var tabCount = labelWidths.length;
    if (tabCount === 0 || containerWidth <= 0) {
        return { mode: 'fit', scrollWidth: 0, tabs: [] };
    }
    var labelsSum = labelWidths.reduce(function (sum, width) { return sum + width; }, 0);
    var minRequiredWidth = labelsSum + 2 * tabCount * minSlotPaddingPx;
    var naturalTabs = labelWidths.map(function (labelWidth, index) {
        var _a;
        return ({
            id: (_a = tabIds[index]) !== null && _a !== void 0 ? _a : "".concat(index),
            labelWidth: labelWidth,
            slotWidth: labelWidth + 2 * minSlotPaddingPx
        });
    });
    var naturalScrollWidth = naturalTabs.reduce(function (sum, tab) { return sum + tab.slotWidth; }, 0);
    if (containerWidth < minRequiredWidth) {
        return {
            mode: 'scroll',
            scrollWidth: naturalScrollWidth,
            tabs: naturalTabs
        };
    }
    var slotPadding = (containerWidth - labelsSum) / (2 * tabCount);
    return {
        mode: 'fit',
        scrollWidth: containerWidth,
        tabs: labelWidths.map(function (labelWidth, index) {
            var _a;
            return ({
                id: (_a = tabIds[index]) !== null && _a !== void 0 ? _a : "".concat(index),
                labelWidth: labelWidth,
                slotWidth: labelWidth + 2 * slotPadding
            });
        })
    };
};
var CalculatedSpacingTabNav = function (_a) {
    var _b;
    var tabs = _a.tabs, activeId = _a.activeId, onChange = _a.onChange;
    var containerRef = useRef(null);
    var measureRef = useRef(null);
    var activeTabRef = useRef(null);
    var _c = useState(null), layout = _c[0], setLayout = _c[1];
    var updateLayout = useCallback(function () {
        var container = containerRef.current;
        var measureRoot = measureRef.current;
        if (!container || !measureRoot || tabs.length === 0)
            return;
        var containerWidth = container.clientWidth;
        var labelWidths = measureCalculatedTabNavLabelWidths(measureRoot);
        var nextLayout = computeCalculatedTabNavLayout(containerWidth, labelWidths, tabs.map(function (tab) { return tab.id; }), MIN_SLOT_PADDING_PX);
        setLayout(nextLayout);
    }, [tabs]);
    useLayoutEffect(function () {
        updateLayout();
    }, [updateLayout]);
    useLayoutEffect(function () {
        var container = containerRef.current;
        if (!container)
            return;
        var observer = new ResizeObserver(function () {
            updateLayout();
        });
        observer.observe(container);
        return function () { return observer.disconnect(); };
    }, [updateLayout]);
    useLayoutEffect(function () {
        var _a;
        (_a = activeTabRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
        });
    }, [activeId, layout]);
    var isScrollMode = (layout === null || layout === void 0 ? void 0 : layout.mode) === 'scroll';
    return (_jsxs("div", __assign({ ref: containerRef, className: "relative w-full min-w-0" }, { children: [_jsx("div", __assign({ ref: measureRef, className: "pointer-events-none absolute -left-[9999px] top-0 opacity-0", "aria-hidden": true }, { children: tabs.map(function (tab) { return (_jsx("span", __assign({ "data-tab-label-measure": true, className: CALCULATED_TAB_NAV_LABEL_CLASS }, { children: tab.name }), tab.id)); }) })), _jsx("div", __assign({ className: classNames('w-full min-w-0', isScrollMode && 'overflow-x-auto') }, { children: _jsx("nav", __assign({ className: classNames('relative flex border-b-2 border-slate-200', isScrollMode ? 'w-max' : 'w-full'), style: isScrollMode && (layout === null || layout === void 0 ? void 0 : layout.scrollWidth)
                        ? { width: layout.scrollWidth }
                        : undefined, "aria-label": "Tabs" }, { children: ((_b = layout === null || layout === void 0 ? void 0 : layout.tabs) !== null && _b !== void 0 ? _b : tabs.map(function (tab) { return ({ id: tab.id, slotWidth: 0 }); })).map(function (tabLayout) {
                        var tab = tabs.find(function (item) { return item.id === tabLayout.id; });
                        if (!tab)
                            return null;
                        var isActive = tab.id === activeId;
                        return (_jsxs("div", __assign({ className: "group relative -mb-px flex shrink-0 items-center justify-center", style: { width: tabLayout.slotWidth || undefined } }, { children: [_jsx("button", __assign({ ref: isActive ? activeTabRef : undefined, type: "button", "aria-current": isActive ? 'page' : undefined, onClick: function () {
                                        if (!isActive) {
                                            onChange === null || onChange === void 0 ? void 0 : onChange(tab.id);
                                        }
                                    }, className: classNames(CALCULATED_TAB_NAV_LABEL_CLASS, 'relative z-[1] flex w-full items-center justify-center px-0 py-2.5 transition-all duration-200', isActive
                                        ? 'text-indigo-600'
                                        : 'text-slate-500 group-hover:text-indigo-600') }, { children: tab.name })), _jsx("span", { className: classNames('absolute inset-x-0 bottom-0 z-[2] -mb-px border-b-2 border-indigo-500 transition-opacity duration-200', isActive
                                        ? 'opacity-100'
                                        : 'opacity-0 group-hover:opacity-100'), "aria-hidden": true })] }), tab.id));
                    }) })) }))] })));
};
/** ERP / Performance Insights section tabs — calculated-spacing underline nav (design #1). */
export var TabsWithScroll = function (props) {
    var activeTab = props.tabs.find(function (tab) { return tab.id === props.active; });
    var options = props.tabs.map(function (tab) { return ({ id: tab.id, name: tab.name }); });
    var selectedOption = options.find(function (option) { return option.id === props.active; }) || options[0];
    var navTabs = props.tabs.map(function (tab) { return ({ id: tab.id, name: tab.name }); });
    return (_jsxs(_Fragment, { children: [_jsxs("div", __assign({ className: "mb-4 w-full min-w-0" }, { children: [_jsx("div", __assign({ className: "sm:hidden" }, { children: _jsx(Select, { onChange: function (value) { var _a; return value.id && ((_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, value.id.toString())); }, selected: selectedOption, options: options }) })), _jsxs("div", __assign({ className: "flex w-full min-w-0 flex-col lg:flex-row lg:items-start lg:justify-between" }, { children: [_jsx("div", __assign({ className: "hidden min-w-0 flex-1 sm:block" }, { children: _jsx(CalculatedSpacingTabNav, { tabs: navTabs, activeId: props.active, onChange: props.onChange }) })), (activeTab === null || activeTab === void 0 ? void 0 : activeTab.rightContent) ? (_jsx("div", __assign({ className: "m-4 shrink-0 sm:m-2 lg:self-start" }, { children: activeTab.rightContent }))) : null] }))] })), _jsx("div", { children: activeTab ? _jsx(activeTab.Component, {}) : null })] }));
};
